/**
 * FieldVisibilityContext - Persisted Field Preferences per Feed
 * 
 * Controls which fields are shown/hidden in LeadCard and LeadDetailPanel.
 * Persists to localStorage with optional backend sync.
 * 
 * Used by: FeedHome, FeedWorkspace, LeadCard, LeadDetailPanel
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface FieldConfig {
  id: string;
  enabled: boolean;
}

export interface FieldVisibilityState {
  feedId: string | null;
  peopleFields: Record<string, boolean>;
  companyFields: Record<string, boolean>;
  jobsFields: Record<string, boolean>;
  agentFields: Record<string, boolean>;
}

export interface FieldVisibilityContextType {
  // Current state
  enabledPeopleFields: Record<string, boolean>;
  enabledCompanyFields: Record<string, boolean>;
  enabledJobsFields: Record<string, boolean>;
  enabledAgentFields: Record<string, boolean>;
  
  // Actions
  toggleField: (_fieldId: string, _category: 'people' | 'company' | 'jobs' | 'agent') => void;
  setFieldEnabled: (_fieldId: string, _category: 'people' | 'company' | 'jobs' | 'agent', _enabled: boolean) => void;
  setFeedId: (_feedId: string | null) => void;
  isFieldEnabled: (_fieldId: string, _category: 'people' | 'company' | 'jobs' | 'agent') => boolean;
  resetToDefaults: () => void;
  
  // Bulk operations
  enableAllFields: (_category: 'people' | 'company' | 'jobs' | 'agent') => void;
  disableAllFields: (_category: 'people' | 'company' | 'jobs' | 'agent') => void;
  
  // Current feed
  currentFeedId: string | null;
}

// =============================================================================
// DEFAULT FIELD CONFIGURATIONS
// =============================================================================

// Default enabled fields for people results
export const DEFAULT_PEOPLE_FIELDS: Record<string, boolean> = {
  fullName: true,
  jobTitle: true,
  companyName: true,
  email: true,
  phone: true,
  location: true,
  industry: true,
  linkedinUrl: true,
  managementLevel: true,
  experienceYears: true,
  education: true,
  employmentHistory: true,
  skills: true,
  certifications: false,
  profilePicture: true,
  summary: true, // Show bio/summary when API returns it (ContactOut, etc.)
};

// Default enabled fields for company results
export const DEFAULT_COMPANY_FIELDS: Record<string, boolean> = {
  companyName: true,
  industry: true,
  employeeCount: true,
  revenue: true,
  website: true,
  location: true,
  companyLogo: true,
  description: false,
  socialLinks: false,
};

// Default enabled fields for job results
export const DEFAULT_JOBS_FIELDS: Record<string, boolean> = {
  jobTitle: true,
  companyName: true,
  salary: true,
  location: true,
  remoteType: true,
  postedDate: true,
  sourceUrl: true,
  requirements: false,
  benefits: false,
};

// Default agent-powered fields (AI enrichment)
export const DEFAULT_AGENT_FIELDS: Record<string, boolean> = {
  companyJobs: false,
  companyNews: false,
  reasonToCall: true,
  matchScore: true,
  demandScore: false,
  linkedinActivity: false,
};

// =============================================================================
// CONTEXT
// =============================================================================

const FieldVisibilityContext = createContext<FieldVisibilityContextType | null>(null);

// =============================================================================
// STORAGE HELPERS
// =============================================================================

const STORAGE_KEY_PREFIX = 'eq_field_visibility_';

function getStorageKey(feedId: string | null): string {
  return feedId ? `${STORAGE_KEY_PREFIX}${feedId}` : `${STORAGE_KEY_PREFIX}global`;
}

function loadFromStorage(feedId: string | null): FieldVisibilityState | null {
  try {
    const key = getStorageKey(feedId);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.error('Failed to load field visibility from storage:', err);
  }
  return null;
}

function saveToStorage(feedId: string | null, state: FieldVisibilityState): void {
  try {
    const key = getStorageKey(feedId);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (err) {
    console.error('Failed to save field visibility to storage:', err);
  }
}

// =============================================================================
// PROVIDER
// =============================================================================

interface FieldVisibilityProviderProps {
  children: ReactNode;
  initialFeedId?: string | null;
}

export function FieldVisibilityProvider({ children, initialFeedId = null }: FieldVisibilityProviderProps) {
  const [currentFeedId, setCurrentFeedId] = useState<string | null>(initialFeedId);
  const [enabledPeopleFields, setEnabledPeopleFields] = useState<Record<string, boolean>>(DEFAULT_PEOPLE_FIELDS);
  const [enabledCompanyFields, setEnabledCompanyFields] = useState<Record<string, boolean>>(DEFAULT_COMPANY_FIELDS);
  const [enabledJobsFields, setEnabledJobsFields] = useState<Record<string, boolean>>(DEFAULT_JOBS_FIELDS);
  const [enabledAgentFields, setEnabledAgentFields] = useState<Record<string, boolean>>(DEFAULT_AGENT_FIELDS);

  // Load state when feed changes
  useEffect(() => {
    const stored = loadFromStorage(currentFeedId);
    if (stored) {
      setEnabledPeopleFields({ ...DEFAULT_PEOPLE_FIELDS, ...stored.peopleFields });
      setEnabledCompanyFields({ ...DEFAULT_COMPANY_FIELDS, ...stored.companyFields });
      setEnabledJobsFields({ ...DEFAULT_JOBS_FIELDS, ...stored.jobsFields });
      setEnabledAgentFields({ ...DEFAULT_AGENT_FIELDS, ...stored.agentFields });
    } else {
      // Reset to defaults for new feed
      setEnabledPeopleFields(DEFAULT_PEOPLE_FIELDS);
      setEnabledCompanyFields(DEFAULT_COMPANY_FIELDS);
      setEnabledJobsFields(DEFAULT_JOBS_FIELDS);
      setEnabledAgentFields(DEFAULT_AGENT_FIELDS);
    }
  }, [currentFeedId]);

  // Save state when it changes
  useEffect(() => {
    const state: FieldVisibilityState = {
      feedId: currentFeedId,
      peopleFields: enabledPeopleFields,
      companyFields: enabledCompanyFields,
      jobsFields: enabledJobsFields,
      agentFields: enabledAgentFields,
    };
    saveToStorage(currentFeedId, state);
  }, [currentFeedId, enabledPeopleFields, enabledCompanyFields, enabledJobsFields, enabledAgentFields]);

  // Get the setter for a category
  const getFieldsSetter = useCallback((category: 'people' | 'company' | 'jobs' | 'agent') => {
    switch (category) {
      case 'people': return setEnabledPeopleFields;
      case 'company': return setEnabledCompanyFields;
      case 'jobs': return setEnabledJobsFields;
      case 'agent': return setEnabledAgentFields;
    }
  }, []);

  // Get the current fields for a category
  const getFields = useCallback((category: 'people' | 'company' | 'jobs' | 'agent') => {
    switch (category) {
      case 'people': return enabledPeopleFields;
      case 'company': return enabledCompanyFields;
      case 'jobs': return enabledJobsFields;
      case 'agent': return enabledAgentFields;
    }
  }, [enabledPeopleFields, enabledCompanyFields, enabledJobsFields, enabledAgentFields]);

  // Toggle a field
  const toggleField = useCallback((fieldId: string, category: 'people' | 'company' | 'jobs' | 'agent') => {
    const setter = getFieldsSetter(category);
    setter(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }));
  }, [getFieldsSetter]);

  // Set a field's enabled state
  const setFieldEnabled = useCallback((fieldId: string, category: 'people' | 'company' | 'jobs' | 'agent', enabled: boolean) => {
    const setter = getFieldsSetter(category);
    setter(prev => ({
      ...prev,
      [fieldId]: enabled,
    }));
  }, [getFieldsSetter]);

  // Check if a field is enabled
  const isFieldEnabled = useCallback((fieldId: string, category: 'people' | 'company' | 'jobs' | 'agent') => {
    const fields = getFields(category);
    return fields[fieldId] ?? true; // Default to true if not defined
  }, [getFields]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setEnabledPeopleFields(DEFAULT_PEOPLE_FIELDS);
    setEnabledCompanyFields(DEFAULT_COMPANY_FIELDS);
    setEnabledJobsFields(DEFAULT_JOBS_FIELDS);
    setEnabledAgentFields(DEFAULT_AGENT_FIELDS);
  }, []);

  // Enable all fields in a category
  const enableAllFields = useCallback((category: 'people' | 'company' | 'jobs' | 'agent') => {
    const setter = getFieldsSetter(category);
    setter(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        updated[key] = true;
      }
      return updated;
    });
  }, [getFieldsSetter]);

  // Disable all fields in a category
  const disableAllFields = useCallback((category: 'people' | 'company' | 'jobs' | 'agent') => {
    const setter = getFieldsSetter(category);
    setter(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        updated[key] = false;
      }
      return updated;
    });
  }, [getFieldsSetter]);

  // Set current feed
  const setFeedId = useCallback((feedId: string | null) => {
    setCurrentFeedId(feedId);
  }, []);

  const value: FieldVisibilityContextType = {
    enabledPeopleFields,
    enabledCompanyFields,
    enabledJobsFields,
    enabledAgentFields,
    toggleField,
    setFieldEnabled,
    setFeedId,
    isFieldEnabled,
    resetToDefaults,
    enableAllFields,
    disableAllFields,
    currentFeedId,
  };

  return (
    <FieldVisibilityContext.Provider value={value}>
      {children}
    </FieldVisibilityContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useFieldVisibility(): FieldVisibilityContextType {
  const context = useContext(FieldVisibilityContext);
  if (!context) {
    throw new Error('useFieldVisibility must be used within a FieldVisibilityProvider');
  }
  return context;
}

// Optional hook that returns null if not in provider (for optional usage)
export function useFieldVisibilityOptional(): FieldVisibilityContextType | null {
  return useContext(FieldVisibilityContext);
}

export default FieldVisibilityContext;

